// Sistema centralizado de captura de erros (suporte interno - Construzilla)
// IMPORTANTE: nunca pode quebrar o app. Toda chamada é silenciosa.

import { supabase as internalSupabase } from '@/integrations/supabase/client'

const APP_VERSION = '1.0.0'
const ENV: 'development' | 'staging' | 'production' =
  (import.meta.env.MODE === 'development' ? 'development' : 'production')

type Severity = 'low' | 'medium' | 'high' | 'critical'

export interface LogContext {
  page?: string
  module?: string
  action?: string
  severity?: Severity
  errorCode?: string | null
  companyId?: string | null
  obraId?: string | null
  userId?: string | null
  userEmail?: string | null
  requestPayload?: unknown
  responsePayload?: unknown
}

const SENSITIVE_KEYS = [
  'password', 'senha', 'token', 'access_token', 'refresh_token',
  'authorization', 'secret', 'api_key', 'apikey',
  'cpf', 'rg', 'cnh', 'passport',
  'card_number', 'cardnumber', 'cvv', 'cvc',
  'iban', 'account_number', 'agencia', 'conta_bancaria',
]

function maskSensitive(input: unknown, depth = 0): unknown {
  if (depth > 6 || input == null) return input
  if (Array.isArray(input)) return input.map((v) => maskSensitive(v, depth + 1))
  if (typeof input === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      const lk = k.toLowerCase()
      if (SENSITIVE_KEYS.some((s) => lk.includes(s))) {
        out[k] = '***MASKED***'
      } else {
        out[k] = maskSensitive(v, depth + 1)
      }
    }
    return out
  }
  return input
}

function safeStringifyPayload(payload: unknown): unknown | null {
  if (payload == null) return null
  try {
    const masked = maskSensitive(payload)
    // Garante serializável e tamanho limitado (~32KB)
    const str = JSON.stringify(masked)
    if (str.length > 32_000) return JSON.parse(str.slice(0, 32_000) + '"...TRUNCATED"}')
    return masked
  } catch {
    return null
  }
}

function detectDevice(): string {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  if (/mobile/i.test(ua)) return 'mobile'
  if (/tablet|ipad/i.test(ua)) return 'tablet'
  return 'desktop'
}

async function getCurrentUser(): Promise<{ id: string | null; email: string | null }> {
  try {
    const { data } = await internalSupabase.auth.getUser()
    return { id: data.user?.id ?? null, email: data.user?.email ?? null }
  } catch {
    return { id: null, email: null }
  }
}

function readActiveContextFromDom(): { companyId: string | null; obraId: string | null } {
  // Lê valores expostos via localStorage pelos contextos
  try {
    const companyId = window.localStorage.getItem('cz:company_id') || null
    const obraId = window.localStorage.getItem('cz:obra_id') || null
    return { companyId, obraId }
  } catch {
    return { companyId: null, obraId: null }
  }
}

export async function logSystemError(
  error: unknown,
  context: LogContext = {},
): Promise<void> {
  try {
    const errMessage =
      error instanceof Error ? error.message :
      typeof error === 'string' ? error :
      (() => { try { return JSON.stringify(error) } catch { return 'Erro desconhecido' } })()

    const stack = error instanceof Error ? error.stack ?? null : null

    const { id: userId, email: userEmail } = context.userId !== undefined
      ? { id: context.userId, email: context.userEmail ?? null }
      : await getCurrentUser()

    const ctxFromDom = readActiveContextFromDom()

    const payload = {
      company_id: context.companyId ?? ctxFromDom.companyId,
      obra_id: context.obraId ?? ctxFromDom.obraId,
      user_id: userId,
      user_email: userEmail,
      page: context.page ?? (typeof window !== 'undefined' ? window.location.pathname : null),
      module: context.module ?? null,
      action: context.action ?? null,
      error_message: String(errMessage).slice(0, 4000),
      error_code: context.errorCode ?? null,
      stack_trace: stack ? String(stack).slice(0, 8000) : null,
      request_payload: safeStringifyPayload(context.requestPayload) ?? null,
      response_payload: safeStringifyPayload(context.responsePayload) ?? null,
      browser: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
      device: detectDevice(),
      app_version: APP_VERSION,
      environment: ENV,
      severity: context.severity ?? 'medium',
      status: 'open' as const,
    }

    // Insert silencioso no banco interno (Lovable Cloud)
    await internalSupabase.from('system_error_logs').insert(payload as any)
  } catch {
    // NUNCA propaga
  }
}

// Helper para envolver chamadas Supabase e capturar erros automaticamente
export async function logSupabaseError(
  result: { error: unknown | null },
  context: LogContext,
): Promise<void> {
  if (!result?.error) return
  const err = result.error as { message?: string; code?: string; details?: string }
  await logSystemError(err?.message || 'Supabase error', {
    ...context,
    errorCode: err?.code ?? context.errorCode ?? null,
    responsePayload: err,
  })
}

// Listeners globais (executar uma única vez)
let installed = false
export function installGlobalErrorListeners() {
  if (installed || typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (event) => {
    const err = event.error || event.message
    void logSystemError(err, {
      module: 'window.onerror',
      severity: 'high',
      action: 'global-error',
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    void logSystemError(event.reason ?? 'unhandledrejection', {
      module: 'window.unhandledrejection',
      severity: 'high',
      action: 'unhandled-promise',
    })
  })
}
